import React, { useState, useEffect, useRef, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import JsBarcode from 'jsbarcode';
import { FiX, FiPrinter, FiRefreshCw, FiWifi, FiWifiOff, FiChevronDown } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { imprimirPDFDirecto } from '../../miscelaneo/imprimirPDF';
import { useImpresion } from '../../contexts/ContextoImpresion';
import qzTrayService from '../../services/qzTrayService';
import instanciaAxios from '../../miscelaneo/instanciaAxios';

// ── Constants ────────────────────────────────────────────────────────────────

const TAMANOS_FLEJE = [
  { id: '108x30', label: '108 × 30 mm (2connet)', ancho: 108, alto: 30 },
  { id: '110x30', label: '110 × 30 mm',           ancho: 110, alto: 30 },
  { id: '105x30', label: '105 × 30 mm',           ancho: 105, alto: 30 },
  { id: '90x30',  label: '90 × 30 mm',            ancho: 90,  alto: 30 },
  { id: '70x30',  label: '70 × 30 mm (~7×3 cm)',  ancho: 70,  alto: 30 },
  { id: '70x25',  label: '70 × 25 mm',            ancho: 70,  alto: 25 },
  { id: '75x25',  label: '75 × 25 mm',            ancho: 75,  alto: 25 },
  { id: '60x30',  label: '60 × 30 mm',            ancho: 60,  alto: 30 },
];

const MODOS_IMPRESION = [
  { id: 'zpl',  label: 'ZPL — Zebra / 2connet',       requiereQZ: true },
  { id: 'tspl', label: 'TSPL — TSC',                   requiereQZ: true },
  { id: 'epl2', label: 'EPL2 — Eltron / Zebra antigua', requiereQZ: true },
  { id: 'pdf',  label: 'PDF (navegador)',               requiereQZ: false },
];

const ORIENTACIONES_FLEJE = [
  { id: 'horizontal', label: 'Horizontal' },
  { id: 'vertical',   label: 'Vertical' },
];

const ROTACIONES_ZPL = [
  { id: 'N', label: '0° — Normal' },
  { id: 'R', label: '90° — Derecha (clockwise)' },
  { id: 'B', label: '270° — Izquierda (bottom-up)' },
  { id: 'I', label: '180° — Invertido' },
];

const DEFAULTS = {
  modo_impresion_fleje: 'zpl',
  tamano_fleje: '70x25',
  impresora_fleje: '',
  orientacion_fleje: 'vertical',
  // Preset recomendado para 2connet/Zebra en fleje apaisado.
  rotacion_zpl: 'R',
  espejo_fleje: false,
};

const MM_A_DOTS = 8; // 203 DPI → 8 dots per mm

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatearPrecio(precio) {
  return new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2,
  }).format(Number(precio) || 0);
}

function sanitizarTextoRaw(texto) {
  return (texto || '').replace(/"/g, "'").replace(/[\^~]/g, '');
}

function truncarTexto(texto, maxLen) {
  if (!texto || texto.length <= maxLen) return texto || '';
  return texto.substring(0, maxLen - 1) + '…';
}

function clamp(valor, min, max) {
  return Math.max(min, Math.min(max, valor));
}

function normalizarRotacion(rotacion) {
  const rot = String(rotacion || 'N').toUpperCase();
  return ['N', 'R', 'B', 'I'].includes(rot) ? rot : 'N';
}

function resolvePhysicalSizeMm(tamano, orientacion = 'horizontal') {
  const esVertical = orientacion === 'vertical';
  return {
    widthMm: esVertical ? tamano.alto : tamano.ancho,
    heightMm: esVertical ? tamano.ancho : tamano.alto,
  };
}

function mmToDots(mm) {
  return Math.max(1, Math.round(mm * MM_A_DOTS));
}

function resolveWorkCanvasByRotation(rotacion, pw, ll) {
  const rot = normalizarRotacion(rotacion);
  const intercambiaEjes = rot === 'R' || rot === 'B';
  return {
    rot,
    workWidth: intercambiaEjes ? ll : pw,
    workHeight: intercambiaEjes ? pw : ll,
  };
}

function crearCanvas(ancho, alto) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(ancho));
  canvas.height = Math.max(1, Math.round(alto));
  return canvas;
}

function obtenerContexto2D(canvas, lecturaFrecuente = false) {
  if (!canvas) return null;
  if (lecturaFrecuente) {
    return canvas.getContext('2d', { willReadFrequently: true }) || canvas.getContext('2d');
  }
  return canvas.getContext('2d');
}

function dividirTextoEnLineas(ctx, texto, maxWidth, maxLineas) {
  const limpio = (texto || '').trim();
  if (!limpio) return ['Descripción del producto'];

  const palabras = limpio.split(/\s+/);
  const lineas = [];
  let actual = '';

  for (const palabra of palabras) {
    const propuesta = actual ? `${actual} ${palabra}` : palabra;
    if (ctx.measureText(propuesta).width <= maxWidth) {
      actual = propuesta;
      continue;
    }

    if (actual) lineas.push(actual);

    if (ctx.measureText(palabra).width <= maxWidth) {
      actual = palabra;
      continue;
    }

    let corte = '';
    for (const ch of palabra) {
      const intento = corte + ch;
      if (ctx.measureText(intento).width <= maxWidth) {
        corte = intento;
      } else {
        if (corte) lineas.push(corte);
        corte = ch;
      }
    }
    actual = corte;
  }

  if (actual) lineas.push(actual);
  if (lineas.length <= maxLineas) return lineas;

  const out = lineas.slice(0, maxLineas);
  const idx = maxLineas - 1;
  let ultima = out[idx];
  while (ultima.length > 0 && ctx.measureText(`${ultima}…`).width > maxWidth) {
    ultima = ultima.slice(0, -1);
  }
  out[idx] = `${ultima || ''}…`;
  return out;
}

function generarBarcodeCanvas(codigoBarra, anchoObjetivo, altoObjetivo) {
  const codigo = sanitizarTextoRaw(codigoBarra).trim();
  if (!codigo) return null;

  const canvas = crearCanvas(Math.max(80, anchoObjetivo), Math.max(50, altoObjetivo));
  try {
    const modulosEstimados = Math.max(90, codigo.length * 11 + 35);
    const anchoModulo = clamp(Math.floor(anchoObjetivo / modulosEstimados), 1, 3);
    const altoBarras = Math.max(24, Math.round(altoObjetivo * 0.62));
    const fontSize = clamp(Math.round(altoObjetivo * 0.18), 9, 24);

    JsBarcode(canvas, codigo, {
      format: 'CODE128',
      width: anchoModulo,
      height: altoBarras,
      displayValue: true,
      fontSize,
      margin: 0,
      font: 'monospace',
    });
    return canvas;
  } catch {
    return null;
  }
}

function dibujarFlejeCanonico(ctx, ancho, alto, { descripcion, precio, codigoBarra, unidadMedida }) {
  if (!ctx) return;

  const margen = clamp(Math.round(ancho * 0.02), 4, 24);
  const separador = clamp(Math.round(Math.min(ancho, alto) * 0.01), 1, 3);
  const anchoUtil = Math.max(52, ancho - margen * 2);

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, ancho, alto);

  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, ancho, separador);
  ctx.fillRect(0, alto - separador, ancho, separador);

  // Layout en columna: nombre arriba, precio y unidad debajo.
  const yTop = margen;
  const xContenido = margen;
  const gapDescPrecio = clamp(Math.round(alto * 0.018), 3, 10);
  const unidad = (unidadMedida || '').toUpperCase().trim();
  const unidadTexto = unidad ? `/ ${truncarTexto(unidad, 12)}` : '';
  const gapPrecioUnidad = unidadTexto ? clamp(Math.round(alto * 0.012), 2, 8) : 0;
  const maxTextHeight = Math.max(34, Math.round(alto * 0.56));
  const textoPrecio = formatearPrecio(precio);

  let descFont = clamp(Math.round(alto * 0.16), 10, Math.round(alto * 0.22));
  let precioFont = clamp(Math.round(alto * 0.20), 14, Math.round(alto * 0.32));
  let unidadFont = unidadTexto ? clamp(Math.round(precioFont * 0.42), 8, Math.round(precioFont * 0.58)) : 0;
  let lineasDesc = [];
  let lineHeight = 0;

  const recalcularDescripcion = () => {
    ctx.font = `700 ${descFont}px monospace`;
    lineasDesc = dividirTextoEnLineas(ctx, descripcion || '', anchoUtil, 2);
    lineHeight = Math.round(descFont * 1.1);
  };

  const ajustarPrecioPorAncho = () => {
    while (precioFont > 12) {
      ctx.font = `900 ${precioFont}px monospace`;
      if (ctx.measureText(textoPrecio).width <= anchoUtil) break;
      precioFont -= 1;
    }
  };

  const ajustarUnidadPorAncho = () => {
    if (!unidadTexto) return;
    while (unidadFont > 8) {
      ctx.font = `600 ${unidadFont}px monospace`;
      if (ctx.measureText(unidadTexto).width <= anchoUtil) break;
      unidadFont -= 1;
    }
  };

  const calcularAlturaTexto = () => {
    const descH = lineasDesc.length * lineHeight;
    const unidadH = unidadTexto ? gapPrecioUnidad + unidadFont : 0;
    return descH + gapDescPrecio + precioFont + unidadH;
  };

  recalcularDescripcion();
  ajustarPrecioPorAncho();
  ajustarUnidadPorAncho();

  while (calcularAlturaTexto() > maxTextHeight && (precioFont > 12 || descFont > 10 || unidadFont > 8)) {
    if (precioFont > 12) {
      precioFont -= 1;
    } else if (descFont > 10) {
      descFont -= 1;
      recalcularDescripcion();
    } else if (unidadTexto && unidadFont > 8) {
      unidadFont -= 1;
    }
    ajustarPrecioPorAncho();
    ajustarUnidadPorAncho();
  }

  ctx.fillStyle = '#000000';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.font = `700 ${descFont}px monospace`;
  lineasDesc.forEach((linea, idx) => {
    ctx.fillText(linea, margen, yTop + idx * lineHeight);
  });

  let yCursor = yTop + lineasDesc.length * lineHeight + gapDescPrecio;
  ctx.textAlign = 'left';
  ctx.font = `900 ${precioFont}px monospace`;
  ctx.fillText(textoPrecio, xContenido, yCursor);
  yCursor += precioFont;

  if (unidadTexto) {
    yCursor += gapPrecioUnidad;
    ctx.font = `600 ${unidadFont}px monospace`;
    ctx.fillText(unidadTexto, xContenido, yCursor);
    yCursor += unidadFont;
  }

  // Código de barras centrado en el bloque inferior.
  const barcodeTop = yCursor + clamp(Math.round(alto * 0.02), 4, 12);
  const barcodeAreaH = Math.max(22, alto - barcodeTop - margen - separador);
  const barcodeCanvas = generarBarcodeCanvas(codigoBarra, anchoUtil, barcodeAreaH);

  if (barcodeCanvas) {
    const escala = Math.min(anchoUtil / barcodeCanvas.width, barcodeAreaH / barcodeCanvas.height);
    const drawW = Math.max(1, Math.round(barcodeCanvas.width * escala));
    const drawH = Math.max(1, Math.round(barcodeCanvas.height * escala));
    const drawX = margen;
    const drawY = barcodeTop + Math.round((barcodeAreaH - drawH) / 2);
    ctx.drawImage(barcodeCanvas, drawX, drawY, drawW, drawH);
  } else {
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = `600 ${clamp(Math.round(alto * 0.05), 10, 18)}px monospace`;
    ctx.fillText('SIN CODIGO DE BARRAS', margen, barcodeTop + Math.round(barcodeAreaH / 2));
  }
}

function aplicarEspejoHorizontal(canvas, espejo) {
  if (!espejo || !canvas) return canvas;
  const out = crearCanvas(canvas.width, canvas.height);
  const ctx = obtenerContexto2D(out);
  if (!ctx) return canvas;

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.translate(out.width, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(canvas, 0, 0);
  return out;
}

function aplicarRotacion(canvas, rotacion, anchoFinal, altoFinal) {
  if (!canvas) return null;
  const rot = normalizarRotacion(rotacion);
  const out = crearCanvas(anchoFinal, altoFinal);
  const ctx = obtenerContexto2D(out);
  if (!ctx) return canvas;

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, out.width, out.height);

  ctx.save();
  if (rot === 'R') {
    ctx.translate(out.width, 0);
    ctx.rotate(Math.PI / 2);
  } else if (rot === 'B') {
    ctx.translate(0, out.height);
    ctx.rotate(-Math.PI / 2);
  } else if (rot === 'I') {
    ctx.translate(out.width, out.height);
    ctx.rotate(Math.PI);
  }
  ctx.drawImage(canvas, 0, 0);
  ctx.restore();

  return out;
}

function renderizarFlejeCanvas({
  anchoPx,
  altoPx,
  descripcion,
  precio,
  codigoBarra,
  unidadMedida,
  rotacion = 'N',
  espejo = false,
}) {
  const { rot, workWidth, workHeight } = resolveWorkCanvasByRotation(rotacion, anchoPx, altoPx);
  const workCanvas = crearCanvas(workWidth, workHeight);
  const workCtx = obtenerContexto2D(workCanvas);
  if (!workCtx) return workCanvas;

  dibujarFlejeCanonico(workCtx, workWidth, workHeight, {
    descripcion,
    precio,
    codigoBarra,
    unidadMedida,
  });

  const espejado = aplicarEspejoHorizontal(workCanvas, espejo);
  return aplicarRotacion(espejado, rot, anchoPx, altoPx);
}

function canvasToGFA(canvas) {
  const ctx = obtenerContexto2D(canvas, true);
  if (!ctx) return { bytesPerRow: 0, totalBytes: 0, hexData: '' };

  const { width, height } = canvas;
  const image = ctx.getImageData(0, 0, width, height);
  const bytesPerRow = Math.ceil(width / 8);
  const totalBytes = bytesPerRow * height;
  const hex = new Array(totalBytes);
  let pos = 0;

  for (let y = 0; y < height; y += 1) {
    for (let b = 0; b < bytesPerRow; b += 1) {
      let byteVal = 0;
      for (let bit = 0; bit < 8; bit += 1) {
        const x = b * 8 + bit;
        if (x >= width) continue;
        const i = (y * width + x) * 4;
        const r = image.data[i];
        const g = image.data[i + 1];
        const bl = image.data[i + 2];
        const a = image.data[i + 3];
        const luma = 0.299 * r + 0.587 * g + 0.114 * bl;
        const esNegro = a > 0 && luma < 170;
        if (esNegro) byteVal |= 1 << (7 - bit);
      }
      hex[pos] = byteVal.toString(16).toUpperCase().padStart(2, '0');
      pos += 1;
    }
  }

  return { bytesPerRow, totalBytes, hexData: hex.join('') };
}

function generarZPLDesdeCanvas(canvas, pw, ll) {
  const { bytesPerRow, totalBytes, hexData } = canvasToGFA(canvas);
  const lines = [
    '^XA',
    '^CI28',
    `^PW${pw}`,
    `^LL${ll}`,
    '^LH0,0',
    `^FO0,0^GFA,${totalBytes},${totalBytes},${bytesPerRow},${hexData}`,
    '^XZ',
  ];
  return lines.join('\n');
}

// ── Preview (same rendering pipeline as ZPL) ─────────────────────────────────

function PreviewFleje({
  descripcion,
  precio,
  codigoBarra,
  unidadMedida,
  tamano,
  orientacion,
  rotacion,
  espejo,
}) {
  const canvasRef = useRef(null);
  const { widthMm, heightMm } = resolvePhysicalSizeMm(tamano, orientacion);
  const MAX_DIM = 370;
  const scale = MAX_DIM / Math.max(widthMm, heightMm);
  const W = Math.max(110, Math.round(widthMm * scale));
  const H = Math.max(70, Math.round(heightMm * scale));

  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;

    const render = renderizarFlejeCanvas({
      anchoPx: W,
      altoPx: H,
      descripcion,
      precio,
      codigoBarra,
      unidadMedida,
      rotacion,
      espejo,
    });
    if (!render) return;

    canvasEl.width = W;
    canvasEl.height = H;
    const ctx = obtenerContexto2D(canvasEl);
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(render, 0, 0, W, H);
  }, [W, H, descripcion, precio, codigoBarra, unidadMedida, rotacion, espejo]);

  return (
    <div className="mx-auto bg-white overflow-hidden" style={{ width: W, height: H, border: '1.5px solid #000' }}>
      <canvas ref={canvasRef} width={W} height={H} style={{ width: W, height: H, display: 'block' }} />
    </div>
  );
}

// ── PDF generator (same visual layout as preview/ZPL) ────────────────────────

function generarPDFFleje(
  descripcion,
  precio,
  codigoBarra,
  unidadMedida,
  tamano,
  orientacion = 'horizontal',
  rotacion = 'N',
  espejo = false
) {
  const { widthMm, heightMm } = resolvePhysicalSizeMm(tamano, orientacion);
  const doc = new jsPDF({
    unit: 'mm',
    format: [widthMm, heightMm],
    orientation: 'portrait',
  });

  const canvas = renderizarFlejeCanvas({
    anchoPx: mmToDots(widthMm),
    altoPx: mmToDots(heightMm),
    descripcion,
    precio,
    codigoBarra,
    unidadMedida,
    rotacion,
    espejo,
  });

  if (canvas) {
    const imgData = canvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', 0, 0, widthMm, heightMm, undefined, 'FAST');
  }

  return doc.output('datauristring').split(',')[1];
}

// ── ZPL generator (canvas -> mono -> ^GFA) ───────────────────────────────────

function generarZPLFleje(
  descripcion,
  precio,
  codigoBarra,
  unidadMedida,
  tamano,
  orientacion = 'horizontal',
  rotacion = 'N',
  espejo = false
) {
  const { widthMm, heightMm } = resolvePhysicalSizeMm(tamano, orientacion);
  const pw = mmToDots(widthMm);
  const ll = mmToDots(heightMm);

  const canvas = renderizarFlejeCanvas({
    anchoPx: pw,
    altoPx: ll,
    descripcion,
    precio,
    codigoBarra,
    unidadMedida,
    rotacion,
    espejo,
  });

  if (!canvas) return '';
  return generarZPLDesdeCanvas(canvas, pw, ll);
}

// ── EPL2 generator ───────────────────────────────────────────────────────────

const EPL2_FONTS = {
  1: { w: 8, h: 12 }, 2: { w: 10, h: 16 }, 3: { w: 12, h: 20 },
  4: { w: 14, h: 24 }, 5: { w: 32, h: 48 },
};

function centrarEPL2(pw, texto, font, hmult) {
  const textW = texto.length * EPL2_FONTS[font].w * hmult;
  return Math.max(0, Math.round((pw - textW) / 2));
}

function generarEPL2Fleje(descripcion, precio, codigoBarra, unidadMedida, tamano, orientacion) {
  const { widthMm, heightMm } = resolvePhysicalSizeMm(tamano, orientacion);
  const pw = mmToDots(widthMm);
  const ll = mmToDots(heightMm);
  const desc = truncarTexto(sanitizarTextoRaw(descripcion), 40);

  const lines = [];
  lines.push('N');
  lines.push(`q${pw}`);
  lines.push(`Q${ll},24`);

  // Description
  let descFont, descHm, descVm;
  if (desc.length <= 15)      { descFont = 3; descHm = 2; descVm = 2; }
  else if (desc.length <= 25) { descFont = 3; descHm = 1; descVm = 2; }
  else if (desc.length <= 35) { descFont = 3; descHm = 1; descVm = 1; }
  else                        { descFont = 2; descHm = 1; descVm = 1; }

  const xDesc = centrarEPL2(pw, desc, descFont, descHm);
  const yDesc = Math.round(ll * 0.04);
  lines.push(`A${xDesc},${yDesc},0,${descFont},${descHm},${descVm},N,"${desc}"`);

  // Barcode (Code128)
  if (codigoBarra) {
    const barH = Math.round(ll * 0.25);
    const yBar = Math.round(ll * 0.30);
    const barNarrow = 2;
    const barWide = 4;
    const barcode = sanitizarTextoRaw(codigoBarra);
    const approxW = barcode.length * (barNarrow + barWide) * 3;
    const xBar = Math.max(0, Math.round((pw - approxW) / 2));
    lines.push(`B${xBar},${yBar},0,1,${barNarrow},${barWide},${barH},B,"${barcode}"`);
  }

  // Price
  let textoPrecio = formatearPrecio(precio);
  if (unidadMedida) textoPrecio += ` / ${unidadMedida}`;
  const xPrecio = centrarEPL2(pw, textoPrecio, 5, 1);
  const yPrecio = Math.round(ll * 0.68);
  lines.push(`A${xPrecio},${yPrecio},0,5,1,1,N,"${textoPrecio}"`);

  lines.push('P1');
  return lines.join('\n');
}

// ── TSPL generator ───────────────────────────────────────────────────────────

const TSPL_FONTS = {
  '1': { w: 8, h: 12 }, '2': { w: 12, h: 20 }, '3': { w: 16, h: 24 },
  '4': { w: 24, h: 32 }, '5': { w: 32, h: 48 },
};

function centrarTSPL(pw, texto, fontId, xmult) {
  const textW = texto.length * TSPL_FONTS[fontId].w * xmult;
  return Math.max(0, Math.round((pw - textW) / 2));
}

function generarTSPLFleje(descripcion, precio, codigoBarra, unidadMedida, tamano, orientacion) {
  const { widthMm, heightMm } = resolvePhysicalSizeMm(tamano, orientacion);
  const pw = mmToDots(widthMm);
  const ll = mmToDots(heightMm);
  const desc = truncarTexto(sanitizarTextoRaw(descripcion), 40);

  const lines = [];
  lines.push(`SIZE ${widthMm} mm, ${heightMm} mm`);
  lines.push('GAP 2 mm, 0');
  lines.push('CLS');

  // Description
  let descFontId, descXm, descYm;
  if (desc.length <= 15)      { descFontId = '3'; descXm = 2; descYm = 2; }
  else if (desc.length <= 25) { descFontId = '3'; descXm = 1; descYm = 2; }
  else if (desc.length <= 35) { descFontId = '3'; descXm = 1; descYm = 1; }
  else                        { descFontId = '2'; descXm = 1; descYm = 1; }

  const xDesc = centrarTSPL(pw, desc, descFontId, descXm);
  const yDesc = Math.round(ll * 0.04);
  lines.push(`TEXT ${xDesc},${yDesc},"${descFontId}",0,${descXm},${descYm},"${desc}"`);

  // Barcode (Code128)
  if (codigoBarra) {
    const barH = Math.round(ll * 0.25);
    const yBar = Math.round(ll * 0.30);
    const barcode = sanitizarTextoRaw(codigoBarra);
    const approxW = barcode.length * 11 * 2;
    const xBar = Math.max(0, Math.round((pw - approxW) / 2));
    lines.push(`BARCODE ${xBar},${yBar},"128",${barH},1,0,2,4,"${barcode}"`);
  }

  // Price
  let textoPrecio = formatearPrecio(precio);
  if (unidadMedida) textoPrecio += ` / ${unidadMedida}`;
  const xPrecio = centrarTSPL(pw, textoPrecio, '5', 1);
  const yPrecio = Math.round(ll * 0.68);
  lines.push(`TEXT ${xPrecio},${yPrecio},"5",0,1,1,"${textoPrecio}"`);

  lines.push('PRINT 1');
  return lines.join('\n');
}

// ── Dispatch map ─────────────────────────────────────────────────────────────

const GENERADORES_RAW = {
  zpl: generarZPLFleje,
  epl2: generarEPL2Fleje,
  tspl: generarTSPLFleje,
};

// ── Modal ────────────────────────────────────────────────────────────────────

export default function ModalFlejeProducto({ visible, onCerrar, descripcion, precio, codigoBarra, unidadMedida }) {
  const { qzConectado, impresoras, impresoraTermica, refrescarImpresoras } = useImpresion();

  const [tamanoId, setTamanoId] = useState(DEFAULTS.tamano_fleje);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [modoImpresion, setModoImpresion] = useState(DEFAULTS.modo_impresion_fleje);
  const [impresoraSeleccionada, setImpresoraSeleccionada] = useState('');
  const [refrescando, setRefrescando] = useState(false);
  const [configCargada, setConfigCargada] = useState(false);
  const [orientacion, setOrientacion] = useState(DEFAULTS.orientacion_fleje);
  const [rotacionZPL, setRotacionZPL] = useState(DEFAULTS.rotacion_zpl);
  const [espejo, setEspejo] = useState(DEFAULTS.espejo_fleje);

  const tamano = TAMANOS_FLEJE.find((t) => t.id === tamanoId) || TAMANOS_FLEJE[0];
  const modoConfig = MODOS_IMPRESION.find((m) => m.id === modoImpresion) || MODOS_IMPRESION[0];
  const esModoDireto = modoConfig.requiereQZ;

  const cargarConfigTenant = useCallback(async () => {
    if (configCargada) return;
    try {
      const { data } = await instanciaAxios.get('/miscelaneo/configuracion');
      setModoImpresion(data.modo_impresion_fleje || DEFAULTS.modo_impresion_fleje);
      setTamanoId(data.tamano_fleje || DEFAULTS.tamano_fleje);
      if (data.impresora_fleje) {
        setImpresoraSeleccionada(data.impresora_fleje);
      }
    } catch {
      // fallback to hardcoded defaults (2connet 2C-LP427B compatible)
    } finally {
      setConfigCargada(true);
    }
  }, [configCargada]);

  useEffect(() => {
    if (visible) cargarConfigTenant();
  }, [visible, cargarConfigTenant]);

  useEffect(() => {
    if (esModoDireto && !impresoraSeleccionada) {
      setImpresoraSeleccionada(impresoraTermica || (impresoras.length > 0 ? impresoras[0] : ''));
    }
  }, [esModoDireto, impresoraTermica, impresoras, impresoraSeleccionada]);

  function handleCerrar() {
    onCerrar();
  }

  async function handleRefrescarImpresoras() {
    setRefrescando(true);
    try { await refrescarImpresoras(); } finally { setRefrescando(false); }
  }

  async function handleImprimir() {
    setImprimiendo(true);
    try {
      if (esModoDireto) {
        if (!impresoraSeleccionada) {
          toast.error('Seleccione una impresora');
          return;
        }
        const generador = GENERADORES_RAW[modoImpresion];
        if (!generador) {
          toast.error('Modo de impresión no soportado');
          return;
        }
        const rawData = generador(descripcion, precio, codigoBarra, unidadMedida, tamano, orientacion, rotacionZPL, espejo);
        const ok = await qzTrayService.imprimirRawPlain(rawData, impresoraSeleccionada);
        if (ok) toast.success('Fleje enviado a la impresora');
        else toast.error('Error al enviar a la impresora');
      } else {
        const base64 = generarPDFFleje(descripcion, precio, codigoBarra, unidadMedida, tamano, orientacion, rotacionZPL, espejo);
        imprimirPDFDirecto(base64);
      }
    } finally {
      setTimeout(() => setImprimiendo(false), 600);
    }
  }

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 border border-gray-200 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="bg-gray-800 p-2.5 rounded-xl">
              <FiPrinter className="text-white text-lg" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-800">Imprimir Fleje</h3>
              <p className="text-xs text-gray-500">Etiqueta de góndola</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCerrar}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">

          {/* Preview */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Vista previa</p>
            <PreviewFleje
              descripcion={descripcion}
              precio={precio}
              codigoBarra={codigoBarra}
              unidadMedida={unidadMedida}
              tamano={tamano}
              orientacion={orientacion}
              rotacion={rotacionZPL}
              espejo={espejo}
            />
          </div>

          {/* Printer section */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
              <span className="text-sm font-medium text-gray-700">{modoConfig.label}</span>
              <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                esModoDireto
                  ? (qzConectado ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700')
                  : 'bg-blue-100 text-blue-700'
              }`}>
                {esModoDireto ? (qzConectado ? <FiWifi size={11} /> : <FiWifiOff size={11} />) : <FiPrinter size={11} />}
                {esModoDireto ? (qzConectado ? 'QZ conectado' : 'Sin QZ') : 'Navegador'}
              </div>
            </div>

            {esModoDireto && (
              <div className="px-4 py-3 border-t border-gray-200 space-y-3">
                {!qzConectado ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2.5">
                    <p className="text-xs text-yellow-700">
                      QZ Tray no está conectado. Inicie QZ Tray para impresión directa.
                    </p>
                    <button
                      type="button"
                      onClick={handleRefrescarImpresoras}
                      disabled={refrescando}
                      className="mt-1.5 text-xs text-yellow-800 underline hover:text-yellow-900 disabled:opacity-50"
                    >
                      {refrescando ? 'Conectando...' : 'Intentar conectar'}
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Impresora
                      </label>
                      <button
                        type="button"
                        onClick={handleRefrescarImpresoras}
                        disabled={refrescando}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                      >
                        <FiRefreshCw size={11} className={refrescando ? 'animate-spin' : ''} />
                        Refrescar
                      </button>
                    </div>
                    {impresoras.length > 0 ? (
                      <select
                        value={impresoraSeleccionada}
                        onChange={(e) => setImpresoraSeleccionada(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-shadow"
                      >
                        <option value="">Seleccionar impresora...</option>
                        {impresoras.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-xs text-gray-500 bg-gray-100 rounded-lg px-3 py-2">
                        No se encontraron impresoras. Presione Refrescar.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Advanced options */}
            <details className="border-t border-gray-200">
              <summary className="flex items-center gap-1.5 px-4 py-2 text-xs text-gray-400 hover:text-gray-600 cursor-pointer select-none">
                <FiChevronDown size={12} />
                Opciones avanzadas
              </summary>
              <div className="px-4 pb-3 pt-1 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Modo</label>
                    <select
                      value={modoImpresion}
                      onChange={(e) => {
                        setModoImpresion(e.target.value);
                        setImpresoraSeleccionada('');
                      }}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-xs bg-white"
                    >
                      {MODOS_IMPRESION.map((m) => (
                        <option key={m.id} value={m.id}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Tamaño</label>
                    <select
                      value={tamanoId}
                      onChange={(e) => setTamanoId(e.target.value)}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-xs bg-white"
                    >
                      {TAMANOS_FLEJE.map((t) => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Orientación</label>
                  <select
                    value={orientacion}
                    onChange={(e) => setOrientacion(e.target.value)}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-xs bg-white"
                  >
                    {ORIENTACIONES_FLEJE.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-gray-400 leading-snug">
                    Solo cambia el papel (PW/LL). Para girar el contenido use Rotación ZPL.
                  </p>
                </div>
                {modoImpresion === 'zpl' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Rotación ZPL</label>
                      <select
                        value={rotacionZPL}
                        onChange={(e) => setRotacionZPL(e.target.value)}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-xs bg-white"
                      >
                        {ROTACIONES_ZPL.map((r) => (
                          <option key={r.id} value={r.id}>{r.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end pb-0.5">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={espejo}
                          onChange={(e) => setEspejo(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-gray-800 focus:ring-gray-400"
                        />
                        <span className="text-xs font-medium text-gray-500">Espejo (invertir orden)</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </details>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCerrar}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-600 text-sm font-medium rounded-xl hover:bg-white transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleImprimir}
            disabled={imprimiendo || (esModoDireto && (!qzConectado || !impresoraSeleccionada))}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-800 text-white text-sm font-semibold rounded-xl hover:bg-gray-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
          >
            {imprimiendo ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              <FiPrinter size={16} />
            )}
            {esModoDireto ? 'Enviar a impresora' : 'Imprimir Fleje'}
          </button>
        </div>

      </div>
    </div>
  );
}
